import { Injectable } from '@angular/core';
import { UserTrackingService } from '@y42/core/user-tracking';
import { ErrorHandlerService } from '@y42/error-handling';
import { ToastService } from '@y42/toast';
import { UserTrackingLegacyService } from '@y42/user-tracking-legacy';
import { UserQuery } from '@y42/user/state';
import { assertNonNullable } from '@y42/util/assert';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { CreateOrgModel, OrgHttpModel, OrgStoreModel } from '../models';
import { OrgSelectedTrackingEvent } from '../models/org-tracking-events.model';
import { OrgQuery, OrgStore } from '../state';
import { OrgHttpService } from './org-http.service';
import { OrgCreatedTrackingEvent, OrgDeletedTrackingEvent } from './user-tracking';

@Injectable({ providedIn: 'root' })
export class OrgService {
  constructor(
    private readonly orgHttpService: OrgHttpService,
    private readonly orgStore: OrgStore,
    private readonly orgQuery: OrgQuery,
    private readonly userQuery: UserQuery,
    private readonly userTrackingLegacy: UserTrackingLegacyService,
    private readonly userTracking: UserTrackingService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
  ) {}

  setActiveOrg(orgId: OrgStoreModel['id']): void {
    const org = this.orgQuery.getEntity(orgId);

    if (!org) {
      console.debug(`[OrgService]: org id '${orgId}' not found`);
      return;
    }

    if (orgId === this.orgQuery.getActiveId()) {
      return;
    }

    console.debug(`[OrgService]: active org '${org.slug}'`);
    this.orgStore.setActive(org.id);
    this.userTracking.track(new OrgSelectedTrackingEvent());
  }

  getOrgs(): Observable<OrgStoreModel[]> {
    if (this.orgQuery.getCount() > 0) {
      return this.orgQuery.orgs$;
    }

    this.orgStore.setLoading(true);

    return forkJoin([this.getAllOrgs(), this.getAllJoinableOrgs()]).pipe(
      map(([orgs]) => orgs),
      finalize(() => {
        this.orgStore.setLoading(false);
      }),
    );
  }

  createOrg(createOrg: CreateOrgModel): Observable<OrgStoreModel | undefined> {
    return this.orgHttpService.createOrg(createOrg).pipe(
      tap((newOrg) => {
        this.orgStore.add(newOrg);
      }),
      map((newOrg) => this.orgQuery.getEntity(newOrg.id)),
      tap((newOrg) => {
        assertNonNullable(newOrg);
        this.toastService.success(`Org "${newOrg.name}" created successfully`);
        this.trackOrgCreate(newOrg);
      }),
      this.errorHandlerService.catchError('[OrgService.createOrg]: could not create org', () => of(undefined), {
        toast: 'Could not create org',
        context: {
          orgSlug: createOrg.slug,
        },
      }),
    );
  }

  updateOrg(orgId: OrgStoreModel['id'], org: Partial<OrgStoreModel>): Observable<OrgStoreModel | undefined> {
    return this.orgHttpService.updateOrg(orgId, org).pipe(
      tap((updatedOrg) => {
        this.orgStore.update(updatedOrg.id, updatedOrg);
      }),
      map((updatedOrg) => this.orgQuery.getEntity(updatedOrg.id)),
      tap((updatedOrg) => {
        assertNonNullable(updatedOrg);
        this.toastService.success(`Org "${updatedOrg.name}" updated successfully`);
      }),
      this.errorHandlerService.catchError('[OrgService.updateOrg]: could not update org', () => of(undefined), {
        toast: 'Could not update org',
        context: {
          orgId,
        },
      }),
    );
  }

  deleteOrg(orgId: OrgStoreModel['id']): Observable<boolean> {
    const deletedOrg = this.orgQuery.getEntity(orgId);
    if (!deletedOrg) {
      return of(false);
    }

    return this.orgHttpService.deleteOrg(orgId).pipe(
      tap(() => {
        this.orgStore.remove(orgId);

        this.trackOrgDelete(deletedOrg);

        this.toastService.success(`Org "${deletedOrg.name}" deleted successfully`);
      }),
      map(() => true),
      this.errorHandlerService.catchError('[OrgService.deleteOrg]: could not delete org', () => of(false), {
        toast: 'Could not delete org',
        context: {
          orgId,
        },
      }),
    );
  }

  isOrgSlugAvailable(slug: string): Observable<boolean> {
    return this.orgHttpService.isOrgSlugAvailable(slug).pipe(
      this.errorHandlerService.catchError(
        '[OrgService.isOrgSlugAvailable]: could not check org slug',
        () => of(false),
        {
          toast: 'Could not check org slug',
        },
      ),
    );
  }

  isOrgInviteCodeValid(code: string): Observable<boolean> {
    return this.orgHttpService.isOrgInviteCodeValid(code).pipe(
      this.errorHandlerService.catchError(
        '[OrgService.isOrgInviteCodeValid]: could not validate org creation code',
        () => of(false),
        {
          toast: 'Could not validate org creation code',
        },
      ),
    );
  }

  private async trackOrgCreate(org: OrgStoreModel): Promise<void> {
    await this.userTrackingLegacy.trackGroup({
      groupId: org.id.toString(),
      groupName: org.name,
    });

    await this.userTrackingLegacy.track(
      new OrgCreatedTrackingEvent(
        {
          account_name: org.name,
        },
        {
          groupId: `${org.id}`,
        },
      ),
    );
  }

  private async trackOrgDelete(org: OrgStoreModel): Promise<void> {
    await this.userTrackingLegacy.track(new OrgDeletedTrackingEvent({ account_name: org.name }));
  }

  private getAllOrgs(): Observable<OrgStoreModel[]> {
    return this.orgHttpService.getOrgs().pipe(
      tap((orgs) => {
        this.orgStore.set(orgs);
      }),
      // Return orgs sorted by name
      map(() => this.orgQuery.getAll()),
      this.errorHandlerService.catchError(
        '[OrgService.getAllOrgs]: could not fetch orgs',
        () => of([] as OrgStoreModel[]),
        {
          toast: 'Could not fetch orgs',
        },
      ),
    );
  }

  private getAllJoinableOrgs(): Observable<OrgHttpModel[]> {
    const email = this.userQuery.getActiveUser()?.email;

    if (!email) {
      return of([]);
    }

    return this.orgHttpService.getAllJoinableOrgs(email).pipe(
      this.errorHandlerService.catchError(
        '[OrgService.getAllJoinableOrgs]: could not fetch joinable orgs',
        () => of([] as OrgStoreModel[]),
        {
          toast: 'Could not fetch joinable orgs',
        },
      ),
      tap((allJoinableOrgs) => this.orgStore.updateUI({ allJoinableOrgs })),
    );
  }
}
